import React, { useEffect, useState } from 'react';
import {
  Button, Col, Form, Input, InputNumber, Modal, Row, Segmented, Select, Space, Typography, Upload, message,
} from 'antd';
import { DeleteOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons';
import {
  computeCustomerPrice, computeMarginPercent, computeTotalInternalCost,
} from '../utils/inquiryHelpers';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const buildState = (item, products) => {
  const matchedProductId = item?.productId
    || products.find((product) => product.name === item?.productName)?.id
    || null;
  const matchedProduct = products.find((product) => product.id === matchedProductId);
  const baseInternalPrice = item?.baseInternalPrice ?? item?.internalPrice ?? 0;
  const baseCustomerPrice = item?.baseCustomerPrice ?? item?.customerPrice ?? item?.price ?? 0;

  return {
    lineType: item?.lineType || 'Product',
    serviceId: item?.serviceId ?? null,
    logisticsId: item?.logisticsId ?? null,
    linkedToLineId: item?.linkedToLineId ?? null,
    categoryId: item?.categoryId || matchedProduct?.categoryId || null,
    productId: matchedProductId,
    supplierName: item?.supplierName || null,
    quantity: item?.quantity ?? 1,
    baseInternalPrice,
    productionCost: item?.productionCost ?? 0,
    deliveryCost: item?.deliveryCost ?? 0,
    baseCustomerPrice,
    margin: item?.margin ?? computeMarginPercent(baseInternalPrice, baseCustomerPrice),
    discount: item?.discount ?? 0,
    preliminaryDeliveryTime: item?.preliminaryDeliveryTime || '',
    brandingDescription: item?.brandingDescription || '',
    deliveryRequirements: item?.deliveryRequirements || '',
    files: item?.files || [],
  };
};

export default function OfferItemModal({
  open,
  onCancel,
  onSave,
  item = null,
  categories = [],
  products = [],
  suppliers = [],
  supplierProducts = [],
  services = [],
  logisticsItems = [],
  otherItems = [],
}) {
  const [form, setForm] = useState(() => buildState(item, products));

  useEffect(() => {
    if (open) setForm(buildState(item, products));
  }, [open, item, products]);

  const activeCategories = categories.filter((c) => c.status !== 'Inactive');
  const filteredProducts = form.categoryId
    ? products.filter((p) => p.categoryId === form.categoryId && p.status !== 'Inactive')
    : [];
  const selectedProduct = products.find((p) => p.id === form.productId);
  const productSupplierProducts = supplierProducts.filter((sp) => sp.productId === selectedProduct?.id);
  const supplierOptions = productSupplierProducts
    .map((sp) => suppliers.find((supplier) => supplier.id === sp.supplierId))
    .filter(Boolean)
    .filter((supplier) => supplier.status !== 'Inactive');
  const selectedSupplierProduct = productSupplierProducts.find((sp) => (
    suppliers.find((supplier) => supplier.id === sp.supplierId)?.name === form.supplierName
  ));

  const activeServices = services.filter((s) => s.status !== 'Inactive');
  const activeLogisticsItems = logisticsItems.filter((l) => l.status !== 'Inactive');
  const selectedService = activeServices.find((s) => s.id === form.serviceId);
  const isChargeService = form.lineType === 'Service' && selectedService?.serviceType === 'Charge';
  const linkableLines = otherItems.filter((i) => (i.lineType || 'Product') === 'Product' && i.id !== item?.id);

  const totalInternalCost = computeTotalInternalCost(form.baseInternalPrice, form.productionCost, form.deliveryCost);
  const customerUnitPrice = (Number(form.baseCustomerPrice || 0) + Number(form.productionCost || 0) + Number(form.deliveryCost || 0))
    * (1 - Number(form.discount || 0) / 100);
  const totalCustomerPrice = customerUnitPrice * Number(form.quantity || 0);

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'lineType') {
        next.categoryId = null;
        next.productId = null;
        next.serviceId = null;
        next.logisticsId = null;
        next.linkedToLineId = null;
        next.supplierName = null;
        next.quantity = 1;
        next.baseInternalPrice = 0;
        next.baseCustomerPrice = 0;
        next.margin = 0;
        next.discount = 0;
      }
      if (field === 'categoryId') {
        next.productId = null;
        next.supplierName = null;
        next.baseInternalPrice = 0;
        next.baseCustomerPrice = 0;
        next.margin = 0;
        next.discount = 0;
      }
      if (field === 'productId') {
        next.supplierName = null;
        next.baseInternalPrice = 0;
        next.baseCustomerPrice = 0;
        next.margin = 0;
        next.discount = 0;
      }
      if (field === 'serviceId') {
        // Service items are owned by exactly one supplier (like Products) —
        // no independent choice here, just resolve and lock it.
        const service = activeServices.find((s) => s.id === value);
        const owningSupplier = suppliers.find((s) => s.id === service?.supplierId);
        next.supplierName = owningSupplier?.name || null;
        next.quantity = service?.serviceType === 'Charge' ? 1 : (prev.quantity || 1);
        next.baseInternalPrice = 0;
        next.baseCustomerPrice = 0;
        next.margin = 0;
        next.discount = 0;
      }
      if (field === 'logisticsId') {
        const logisticsItem = activeLogisticsItems.find((l) => l.id === value);
        const owningSupplier = suppliers.find((s) => s.id === logisticsItem?.supplierId);
        next.supplierName = owningSupplier?.name || null;
        next.baseInternalPrice = 0;
        next.baseCustomerPrice = 0;
        next.margin = 0;
        next.discount = 0;
      }
      if (field === 'supplierName' && prev.lineType === 'Product') {
        const supplierProduct = supplierProducts.find((sp) => (
          sp.productId === prev.productId
          && suppliers.find((supplier) => supplier.id === sp.supplierId)?.name === value
        ));
        const internalPrice = supplierProduct?.avgItemPrice ?? 0;
        next.baseInternalPrice = internalPrice;
        next.baseCustomerPrice = internalPrice;
        next.margin = 0;
        next.discount = 0;
      }
      if (field === 'margin') {
        next.baseCustomerPrice = computeCustomerPrice(next.baseInternalPrice, value);
      } else if (field === 'baseCustomerPrice') {
        next.margin = computeMarginPercent(next.baseInternalPrice, value);
      } else if (field === 'baseInternalPrice') {
        next.baseCustomerPrice = computeCustomerPrice(value, next.margin);
      }
      return next;
    });
  };

  const handleOk = () => {
    const lineType = form.lineType || 'Product';
    let productId = form.productId;
    let productName = '';
    let article = '';
    let categoryId = null;

    if (lineType === 'Product') {
      if (!form.productId) {
        message.warning('Please select a product');
        return;
      }
      const product = products.find((p) => p.id === form.productId);
      if (!product) return;
      productName = product.name;
      article = form.supplierName ? (selectedSupplierProduct?.supplierItemNumber || '') : '';
      categoryId = product.categoryId;
    } else if (lineType === 'Service') {
      if (!form.serviceId) {
        message.warning('Please select a service');
        return;
      }
      productId = null;
      productName = selectedService?.name || '';
      article = selectedService?.code || '';
    } else {
      const logisticsItem = activeLogisticsItems.find((l) => l.id === form.logisticsId);
      if (!logisticsItem) {
        message.warning('Please select a logistics item');
        return;
      }
      productId = null;
      productName = logisticsItem.name;
      article = logisticsItem.code;
    }

    const baseInternalPrice = Number(form.baseInternalPrice) || 0;
    const productionCost = Number(form.productionCost) || 0;
    const deliveryCost = Number(form.deliveryCost) || 0;
    const baseCustomerPrice = Number(form.baseCustomerPrice) || 0;
    const discount = Math.min(Math.max(Number(form.discount) || 0, 0), 100);
    const totalInternal = computeTotalInternalCost(baseInternalPrice, productionCost, deliveryCost);
    const customerUnit = (baseCustomerPrice + productionCost + deliveryCost) * (1 - discount / 100);

    onSave?.({
      id: item?.id || Date.now(),
      lineType,
      serviceId: lineType === 'Service' ? form.serviceId : null,
      logisticsId: lineType === 'Logistics' ? form.logisticsId : null,
      linkedToLineId: lineType !== 'Product' ? form.linkedToLineId : null,
      productId,
      productName,
      article,
      categoryId,
      supplierName: form.supplierName || '',
      quantity: form.quantity,
      baseInternalPrice,
      productionCost,
      deliveryCost,
      baseCustomerPrice,
      internalPrice: totalInternal,
      price: customerUnit,
      customerPrice: customerUnit,
      margin: computeMarginPercent(totalInternal, customerUnit),
      discount,
      internalLineTotal: totalInternal * form.quantity,
      lineTotal: customerUnit * form.quantity,
      preliminaryDeliveryTime: form.preliminaryDeliveryTime,
      brandingDescription: form.brandingDescription,
      deliveryRequirements: form.deliveryRequirements,
      notes: [form.brandingDescription, form.deliveryRequirements].filter(Boolean).join(' | '),
      files: form.files || [],
    });
  };

  return (
    <Modal
      title={item ? 'Edit Item' : 'Add Item'}
      open={open}
      onCancel={onCancel}
      width={840}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="save" type="primary" onClick={handleOk}>Save</Button>,
      ]}
    >
      <Form layout="vertical">
        <Row gutter={12}>
          <Col xs={24}>
            <Form.Item label="Line Type">
              <Segmented
                value={form.lineType}
                onChange={(v) => updateField('lineType', v)}
                options={[
                  { label: 'Product', value: 'Product' },
                  { label: 'Service', value: 'Service' },
                  { label: 'Logistics', value: 'Logistics' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        {form.lineType === 'Product' && (
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item label="Category">
              <Select
                placeholder="Select category"
                value={form.categoryId}
                onChange={(v) => updateField('categoryId', v)}
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {activeCategories.map((c) => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Product">
              <Select
                placeholder={form.categoryId ? 'Select product' : 'Select category first'}
                value={form.productId}
                onChange={(v) => updateField('productId', v)}
                disabled={!form.categoryId}
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {filteredProducts.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.code ? `${p.code} - ` : ''}{p.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Supplier">
              <Select
                placeholder={form.productId ? (supplierOptions.length ? 'Select supplier or leave blank' : 'No public supplier available') : 'Select product first'}
                value={form.supplierName}
                onChange={(v) => updateField('supplierName', v)}
                disabled={!form.productId}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {supplierOptions.map((supplier) => (
                  <Option key={supplier.id} value={supplier.name}>
                    {supplier.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        )}
        {form.lineType === 'Service' && (
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item label="Service">
              <Select
                placeholder="Select a service"
                value={form.serviceId}
                onChange={(v) => updateField('serviceId', v)}
                showSearch
                optionFilterProp="children"
              >
                {activeServices.map((s) => (
                  <Option key={s.id} value={s.id}>{`${s.code} — ${s.name}`}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Supplier">
              <Input disabled value={form.supplierName || 'No supplier assigned to this service'} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Applies to product line">
              <Select
                placeholder="Optional — link to a product line"
                value={form.linkedToLineId}
                onChange={(v) => updateField('linkedToLineId', v)}
                allowClear
              >
                {linkableLines.map((line) => (
                  <Option key={line.id} value={line.id}>{line.productName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        )}
        {form.lineType === 'Logistics' && (
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item label="Logistics Item">
              <Select
                placeholder="Select a logistics item"
                value={form.logisticsId}
                onChange={(v) => updateField('logisticsId', v)}
                showSearch
                optionFilterProp="children"
              >
                {activeLogisticsItems.map((l) => (
                  <Option key={l.id} value={l.id}>{`${l.code} — ${l.name}`}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Supplier">
              <Input disabled value={form.supplierName || 'No supplier assigned to this logistics item'} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Applies to product line">
              <Select
                placeholder="Optional — link to a product line"
                value={form.linkedToLineId}
                onChange={(v) => updateField('linkedToLineId', v)}
                allowClear
              >
                {linkableLines.map((line) => (
                  <Option key={line.id} value={line.id}>{line.productName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        )}
        <Row gutter={12}>
          <Col xs={12} sm={6}>
            <Form.Item label="Qty">
              <InputNumber
                min={1}
                disabled={isChargeService}
                value={isChargeService ? 1 : form.quantity}
                onChange={(v) => updateField('quantity', v || 1)}
                style={{ width: '100%' }}
              />
              {isChargeService && <Text type="secondary" style={{ fontSize: 12 }}>Fixed fee — quantity is not applicable</Text>}
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="Base Internal Price">
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                value={form.baseInternalPrice}
                onChange={(v) => updateField('baseInternalPrice', v ?? 0)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="Production Cost">
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                value={form.productionCost}
                onChange={(v) => updateField('productionCost', v ?? 0)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="Delivery Cost">
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                value={form.deliveryCost}
                onChange={(v) => updateField('deliveryCost', v ?? 0)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12} sm={6}>
            <Form.Item label="Base Customer Price">
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                value={form.baseCustomerPrice}
                onChange={(v) => updateField('baseCustomerPrice', v ?? 0)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="Margin %">
              <InputNumber
                step={0.1}
                precision={1}
                value={form.margin}
                onChange={(v) => updateField('margin', v ?? 0)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="Discount %">
              <InputNumber
                min={0}
                max={100}
                step={1}
                precision={0}
                value={form.discount}
                onChange={(v) => updateField('discount', v ?? 0)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="Preliminary Delivery Time">
              <Input
                placeholder="one week"
                value={form.preliminaryDeliveryTime}
                onChange={(e) => updateField('preliminaryDeliveryTime', e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item label="Branding Description">
              <TextArea
                rows={2}
                placeholder="Per-item branding instructions"
                value={form.brandingDescription}
                onChange={(e) => updateField('brandingDescription', e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Delivery Requirements">
              <TextArea
                rows={2}
                placeholder="Per-item delivery specifics"
                value={form.deliveryRequirements}
                onChange={(e) => updateField('deliveryRequirements', e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12} sm={12}>
            <Form.Item label="Total Internal Cost">
              <Text strong>{totalInternalCost.toLocaleString('da-DK')} DKK</Text>
            </Form.Item>
          </Col>
          <Col xs={12} sm={12}>
            <Form.Item label="Total Customer Cost">
              <Text strong>{customerUnitPrice.toLocaleString('da-DK')} DKK</Text>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Item attachments">
          {form.files?.length > 0 && (
            <Space direction="vertical" size={2} style={{ marginBottom: 8, width: '100%' }}>
              {form.files.map((file) => (
                <Space key={file.uid} size={6} style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space size={6}>
                    <FileOutlined />
                    <Text>{file.name}</Text>
                  </Space>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => updateField('files', form.files.filter((f) => f.uid !== file.uid))}
                  />
                </Space>
              ))}
            </Space>
          )}
          <Upload
            multiple
            showUploadList={false}
            beforeUpload={(file) => {
              updateField('files', [
                ...(form.files || []),
                {
                  uid: `${Date.now()}-${file.name}`,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                },
              ]);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>Attach files</Button>
          </Upload>
        </Form.Item>
        {(form.productId || form.serviceId || form.logisticsId) && (
          <div style={{ textAlign: 'right', fontSize: '14px', color: '#666' }}>
            Customer unit: <strong>{customerUnitPrice.toLocaleString('da-DK')} DKK</strong>
            <span style={{ marginLeft: 16 }}>
              Internal total: <strong>{(totalInternalCost * Number(form.quantity || 0)).toLocaleString('da-DK')} DKK</strong>
            </span>
            <span style={{ marginLeft: 16 }}>
              Customer total: <strong>{totalCustomerPrice.toLocaleString('da-DK')} DKK</strong>
            </span>
          </div>
        )}
      </Form>
    </Modal>
  );
}
